import React, { useState, useEffect } from "react";
import { Search, Filter, FileText, ArrowDownRight, ArrowUpRight, ShieldCheck, Clock, Trash2, CalendarRange, X, Download } from "lucide-react";
import { toast } from "sonner";
import api, { money } from "../lib/api";
import { motion } from "framer-motion";
import SearchableSelect from "../components/ui/SearchableSelect";
import { fmt12, fmtDate, todayIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

const role = () => {
  try {
    return (JSON.parse(localStorage.getItem("lss_user") || "{}").role || "owner").toLowerCase();
  } catch {
    return "owner";
  }
};

export default function StockLedgerPage() {
  // Dropping an individual ledger row changes live stock, so that column is
  // owner-only. The whole-ledger clear action has been removed.
  const isOwner = role() === "owner";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [txnType, setTxnType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    fetchLedger();
  }, [txnType, dateFrom, dateTo]);

  const fetchLedger = async (silent = false) => {
    // `silent` keeps the spinner off for the background poll — the table stays
    // readable while it reloads underneath instead of flashing every 30s.
    if (!silent) setLoading(true);
    try {
      // The API defaults to the newest 200 rows. That silently hid older
      // manual stock-outs (there are 245 of them), so "All Movements" and the
      // Manual Stock Out filter both looked incomplete. Ask for the full
      // audit trail; the endpoint caps at 100,000 and the table is small.
      let url = `/inventory/ledger?transaction_type=${txnType}&limit=100000`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      setLogs(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // No manual "Refresh Ledger" button: the ledger is a live audit trail, so it
  // keeps itself current on a poll and whenever the window regains focus.
  useAutoRefresh(() => fetchLedger(true), 30000);

  const handleDeleteRow = async (l) => {
    const qty = Number(l.quantity) || 0;
    const what = qty < 0
      ? `This will put ${Math.abs(qty)} of '${l.product_name}' BACK into inventory and remove the row.`
      : qty > 0
        ? `This will take ${qty} of '${l.product_name}' OUT of inventory and remove the row.`
        : `This will remove the row (no stock change).`;
    if (!window.confirm(`Delete this movement?\n\n${what}`)) return;
    try {
      const res = await api.delete(`/inventory/ledger/${l.id}`);
      if (res?.data?.message) alert(res.data.message);
      fetchLedger(true);
    } catch (e) {
      console.error(e);
      alert("Failed to delete entry: " + (e.response?.data?.detail || e.message));
    }
  };

  const filteredLogs = logs.filter(
    (l) =>
      !search ||
      l.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.product_code?.toLowerCase().includes(search.toLowerCase()) ||
      l.transaction_id?.toLowerCase().includes(search.toLowerCase()) ||
      l.performed_by?.toLowerCase().includes(search.toLowerCase())
  );

  const exportCsv = () => {
    if (!filteredLogs.length) return;
    const esc = (v) => {
      const text = v == null ? "" : String(v);
      return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const moneyCell = (v) => (v == null ? "" : Number(v).toFixed(2));
    const headers = [
      "Txn ID", "Date", "Time", "Product", "Product Code", "Store",
      "Transaction Type", "Quantity", "Opening", "Closing",
      "Unit Price", "Consumption Valuation", "Performed By", "Approved By", "Remarks",
    ];
    const body = filteredLogs.map((l) => [
      l.transaction_id || `TXN-${l.id || ""}`,
      l.date || "",
      l.time || "",
      l.product_name || "",
      l.product_code || "",
      l.store || "",
      (l.transaction_type || "").replace(/_/g, " "),
      l.quantity ?? "",
      l.before_stock ?? "",
      l.after_stock ?? "",
      moneyCell(l.unit_price),
      moneyCell(l.consumption_value),
      l.performed_by || "",
      l.approved_by || "",
      l.remarks || "",
    ].map(esc).join(","));
    const blob = new Blob(["\uFEFF" + [headers.map(esc).join(","), ...body].join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Geetanjali_Stock_Ledger_${todayIST()}.csv`);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success(`Exported ${filteredLogs.length} ledger entries.`);
  };

  const getBadgeStyle = (type, qty) => {
    if (qty > 0) return "bg-emerald-100 text-emerald-900 border-emerald-300";
    if (type === "pos_service_consumption") return "bg-sky-100 text-sky-950 border-sky-300";
    return "bg-slate-100 text-slate-900 border-slate-300";
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div>
          <span className="lss-overline text-amber-800">Audit Trail & Compliance</span>
          <h1 className="font-serif-lux text-3xl font-bold tracking-tight text-slate-950 mt-1">
            Real-Time Stock Movement Ledger
          </h1>
          <p className="text-slate-600 text-sm font-medium mt-1">
            Immutable transaction record for every stock in, POS retail sale, and service BOM consumption.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={exportCsv}
            disabled={!filteredLogs.length}
            title="Export the ledger rows currently shown"
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-[280px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            <input
              type="text"
              placeholder="Search by Product Name, Transaction ID, or Staff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="lss-input lss-search w-full text-xs font-semibold"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Type Filter:</span>
          <SearchableSelect
            options={[
              { value: "all", label: "All Movements" },
              { value: "purchase_order", label: "Purchase Order Inward" },
              { value: "purchase_invoice", label: "Purchase Invoice Inward" },
              { value: "direct_purchase", label: "Direct Purchase" },
              { value: "manual_stock_out", label: "Manual Stock Out" },
              { value: "pos_sale", label: "POS Retail Sale" },
              { value: "pos_service_consumption", label: "POS Service BOM" },
              { value: "audit_adjustment", label: "Stock Audit Adjustment" },
              { value: "MASTER_EXCEL_IMPORT", label: "Opening Stock Import" },
            ]}
            value={txnType}
            onChange={(val) => setTxnType(val)}
            placeholder="All Movements"
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
            className="min-w-[200px]"
          />
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <CalendarRange className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Date Range:</span>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className="lss-input text-xs font-semibold"
            aria-label="From date"
          />
          <span className="text-slate-400 text-xs font-bold">→</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className="lss-input text-xs font-semibold"
            aria-label="To date"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-rose-600 transition-colors"
              aria-label="Clear date range"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Ledger Log Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
            Stock Ledger Entries ({filteredLogs.length})
          </span>
          <span className="text-xs text-slate-500 font-semibold">Strict Real-time Audit Trail</span>
        </div>

        {loading ? (
          <div className="p-4 space-y-4 animate-pulse">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex gap-4">
                <div className="h-10 bg-slate-200 rounded w-1/5"></div>
                <div className="h-10 bg-slate-200 rounded w-1/4"></div>
                <div className="h-10 bg-slate-200 rounded w-1/5"></div>
                <div className="h-10 bg-slate-200 rounded w-1/5"></div>
                <div className="h-10 bg-slate-200 rounded w-1/5"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lss-table">
              <thead>
                <tr>
                  <th>Txn ID & Date</th>
                  <th>Product</th>
                  <th>Store / Location</th>
                  <th>Transaction Type</th>
                  <th className="text-right">Quantity</th>
                  <th className="text-right">Opening</th>
                  <th className="text-right">Closing</th>
                  <th className="text-right" title="Consumption valuation = outbound quantity × current unit price; inward rows are blank">Consumption Valuation</th>
                  <th>Performed By</th>
                  <th>Remarks</th>
                  {isOwner && <th className="text-center">Drop</th>}
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/80 transition-colors">
                    <td>
                      <div className="font-mono font-bold text-slate-950 text-xs">{l.transaction_id || `TXN-${l.id ? l.id.slice(0, 8) : ""}`}</div>
                      <div className="text-[11px] text-slate-500 font-medium">{fmtDate(l.date)} at {fmt12(l.time)}</div>
                    </td>
                    <td>
                      <div className="font-extrabold text-slate-950 text-xs">{l.product_name}</div>
                    </td>
                    <td className="text-xs font-semibold text-slate-700">{l.store || "Main Salon Store"}</td>
                    <td>
                      <span className={`lss-badge text-[10px] uppercase ${getBadgeStyle(l.transaction_type, l.quantity)}`}>
                        {l.transaction_type?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className={`text-right tabular font-extrabold text-xs ${l.quantity > 0 ? "text-emerald-800" : "text-rose-800"}`}>
                      {l.quantity > 0 ? `+${l.quantity}` : l.quantity}
                    </td>
                    <td className="text-right tabular font-semibold text-slate-800 text-xs">
                      {l.before_stock}
                    </td>
                    <td className="text-right tabular font-extrabold text-slate-950 text-xs">
                      {l.after_stock}
                    </td>
                    <td
                      className="text-right tabular text-xs font-bold text-slate-900"
                      title={l.consumption_value != null
                        ? `${Math.abs(Number(l.quantity) || 0).toLocaleString()} × ${money(l.unit_price)}`
                        : "Inward movement"}
                    >
                      {l.consumption_value == null
                        ? <span className="text-slate-300">—</span>
                        : money(l.consumption_value)}
                    </td>
                    <td>
                      <div className="text-xs font-bold text-slate-950">{l.performed_by || "System"}</div>
                      {l.approved_by && <div className="text-[10px] text-slate-500">Appr: {l.approved_by}</div>}
                    </td>
                    <td className="text-xs text-slate-600 whitespace-normal break-words min-w-[16rem]" title={l.remarks}>
                      {l.remarks || "Standard Movement"}
                    </td>
                    {isOwner && (
                      <td className="text-center">
                        <button onClick={() => handleDeleteRow(l)} title="Delete row (stock effect reversed; orphan rows simply deleted)" className="p-1.5 text-rose-600 hover:bg-red-100 rounded-lg transition">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {filteredLogs.length === 0 && (
                  <tr>
                    <td colSpan={isOwner ? 11 : 10} className="text-center py-12 text-slate-500 font-medium">
                      No stock movement entries match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
